import React from 'react';
import { FiX } from 'react-icons/fi';
import useMonitoringHistory from './hooks/useMonitoringHistory';
import APaginationInline from '../../molecules/APaginationInline';
import { Classification, HistoryItem } from '../../../services/monitoringApi';

interface MonitoringHistoryDrawerProps {
    isOpen: boolean;
    item: Classification;
    onClose: () => void;
}

const OPERATION_LABELS: Record<string, string> = {
    MONITORING_EXECUTION: 'Execução de Monitoramento',
    RISK_LEVEL_UPDATED: 'Alteração de Risco',
    CLASSIFICATION_CREATED: 'Cadastro',
    CLASSIFICATION_DEACTIVATED: 'Desativação',
};

const RESULT_BADGE: Record<string, { bg: string; label: string }> = {
    SUCESSO: { bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Sucesso' },
    SUCCESS: { bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Sucesso' },
    FALHA: { bg: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Falha' },
    FAILURE: { bg: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Falha' },
    TIMEOUT: { bg: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Timeout' },
};

const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const HistoryItemCard: React.FC<{ entry: HistoryItem }> = ({ entry }) => {
    const details = entry.details || {};
    const divergenceCount = (details.divergences as unknown[])?.length || 0;
    const resultInfo = RESULT_BADGE[entry.operation_result?.toUpperCase()] || RESULT_BADGE['SUCESSO'];

    const isRiskUpdate = entry.operation_type === 'RISK_LEVEL_UPDATED';
    const oldRisk = details.old_risk_level as string | undefined;
    const newRisk = details.new_risk_level as string | undefined;
    const notes = details.notes as string | undefined;

    return (
        <div className="border-l-2 border-blue-300 dark:border-blue-600 pl-4 pb-4">
            <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(entry.created_at)}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${resultInfo.bg}`}>
                    {resultInfo.label}
                </span>
            </div>

            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {OPERATION_LABELS[entry.operation_type] || entry.operation_type}
            </p>

            {isRiskUpdate && oldRisk && newRisk && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {oldRisk} &rarr; {newRisk}
                </p>
            )}

            {!isRiskUpdate && divergenceCount > 0 && (
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    {divergenceCount} divergência{divergenceCount > 1 ? 's' : ''} detectada{divergenceCount > 1 ? 's' : ''}
                </p>
            )}

            {notes && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                    &ldquo;{notes}&rdquo;
                </p>
            )}

            {entry.user_id && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    por {entry.user_id}
                </p>
            )}
        </div>
    );
};

const MonitoringHistoryDrawer: React.FC<MonitoringHistoryDrawerProps> = ({ isOpen, item, onClose }) => {
    const {
        items, total, page, setPage, totalPages, isLoading,
        dateFrom, setDateFrom, dateTo, setDateTo,
        hasDivergence, setHasDivergence,
    } = useMonitoringHistory(isOpen ? item.person_id : null);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-40 flex justify-end">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />

            {/* Drawer */}
            <div className="relative w-full max-w-md bg-white dark:bg-slate-800 shadow-xl flex flex-col h-full z-50">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Histórico
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {item.person_id}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600"
                        aria-label="Fechar"
                    >
                        <FiX className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                </div>

                {/* Filters */}
                <div className="px-5 py-3 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
                    <div className="flex gap-2 mb-2">
                        <div className="flex-1">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">De</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Até</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={hasDivergence === true}
                            onChange={(e) => setHasDivergence(e.target.checked ? true : undefined)}
                            className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        Apenas com divergência
                    </label>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                    ) : items.length === 0 ? (
                        <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
                            Nenhum registro encontrado
                        </p>
                    ) : (
                        <div className="space-y-1">
                            {items.map((entry) => (
                                <HistoryItemCard key={entry.id} entry={entry} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer pagination */}
                {totalPages > 1 && (
                    <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {total} registro{total !== 1 ? 's' : ''}
                        </span>
                        <APaginationInline
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonitoringHistoryDrawer;
